import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listPublishedTemplates, type TemplateDomain, type TemplateTaskType } from '@/services/templateSystemApi';
import { queryKeys } from '@/lib/queryKeys';
import EmptyState from '@/components/common/EmptyState';
import ContentApiErrorState from '@/components/common/ContentErrorState';
import PageLoading from '@/components/common/PageLoading';

const domainOptions: Array<TemplateDomain | 'all'> = ['all', 'python', 'information_systems', 'networks', 'iot'];
const taskOptions: Array<TemplateTaskType | 'all'> = ['all', 'lesson_plan', 'question_set', 'topology', 'slides', 'mixed'];

export default function TemplateGalleryPage() {
  const [domain, setDomain] = useState<TemplateDomain | 'all'>('all');
  const [taskType, setTaskType] = useState<TemplateTaskType | 'all'>('all');

  const filters = useMemo(
    () => ({
      domain: domain === 'all' ? undefined : domain,
      taskType: taskType === 'all' ? undefined : taskType,
    }),
    [domain, taskType],
  );

  const query = useQuery({
    queryKey: queryKeys.templates.published(filters),
    queryFn: () => listPublishedTemplates(filters),
    retry: 1,
  });

  if (query.isLoading) {
    return <PageLoading message='模板加载中…' />;
  }

  return (
    <div className='max-w-[1080px] mx-auto px-6 py-12'>
      <div className='flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8'>
        <div>
          <h1
            className='font-semibold text-apple-near-black mb-2'
            style={{ fontSize: '2rem', lineHeight: 1.1, letterSpacing: '-0.02em' }}
          >
            教学模板
          </h1>
          <p className='text-apple-gray text-[0.9375rem]'>选择已发布模板并填写参数，快速生成课堂材料。</p>
        </div>
        <Link to='/templates/custom' className='btn btn-secondary'>
          我的自定义模板
        </Link>
      </div>

      <div className='bg-white rounded-2xl p-4 shadow-card-sm mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3'>
        <select className='input' value={domain} onChange={(event) => setDomain(event.target.value as TemplateDomain | 'all')}>
          {domainOptions.map((option) => (
            <option key={option} value={option}>
              {option === 'all' ? '全部学科' : option}
            </option>
          ))}
        </select>
        <select className='input' value={taskType} onChange={(event) => setTaskType(event.target.value as TemplateTaskType | 'all')}>
          {taskOptions.map((option) => (
            <option key={option} value={option}>
              {option === 'all' ? '全部任务' : option}
            </option>
          ))}
        </select>
      </div>

      {query.isError ? (
        <ContentApiErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : null}

      {!query.isError && (query.data ?? []).length === 0 ? (
        <EmptyState title='暂无已发布模板' description='请稍后刷新，或联系管理员发布模板。' />
      ) : null}

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        {(query.data ?? []).map((template) => (
          <article key={template.id} className='bg-white rounded-2xl p-5 shadow-card-sm'>
            <div className='flex items-start justify-between gap-3 mb-3'>
              <h2 className='font-semibold text-apple-near-black text-[1rem]'>{template.name}</h2>
              <span className='text-[0.75rem] text-apple-gray'>v{template.version}</span>
            </div>
            <p className='text-apple-gray text-[0.875rem] mb-4'>{template.description}</p>
            <p className='text-[0.8125rem] text-apple-gray mb-2'>
              {template.domain} · {template.taskType}
            </p>
            {template.tagsJson && template.tagsJson.length > 0 ? (
              <div className='flex flex-wrap gap-2 mb-4'>
                {template.tagsJson.map((tag) => (
                  <span key={tag} className='text-[0.75rem] px-2 py-1 rounded-full bg-apple-light-gray text-apple-gray'>
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}
            <Link to={`/templates/${template.id}/run`} className='btn btn-primary'>
              使用模板
            </Link>
          </article>
        ))}
      </div>
    </div>
  );
}
